import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useSearchParams } from "react-router-dom";
import { Lock as LockIcon } from "lucide-react";
import { AxiosError } from "axios";

import StarterContainer from "@/components/StarterContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FormError } from "@/components/FormError";
import { ErrorBox } from "@/components/ui/error-box";
import { useLoading } from "@/lib/LoadingHook";
import { useAppToast } from "@/hooks/useAppToast";
import { useAppNavigate } from "@/lib/AppNavigatorHook";
import { isBadRequest } from "@/lib/errorUtils";
import { resetPassword } from "@/services/shared/auth/resetPassword.service";

// Define the form data structure
interface ResetPasswordFormInputs {
  newPassword: string;
  confirmPassword: string;
}

// Define the validation schema using yup
const schema = yup.object().shape({
  newPassword: yup
    .string()
    .min(8, "A senha deve ter no mínimo 8 caracteres")
    .required("Nova senha é obrigatória"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "As senhas não coincidem")
    .required("Confirmação de senha é obrigatória"),
});

export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<ResetPasswordFormInputs>({
    resolver: yupResolver(schema),
  });

  const { isLoading, whenLoading } = useLoading();
  const { success, error: toastError } = useAppToast();
  const { goToLogin } = useAppNavigate();

  const [submissionError, setSubmissionError] = React.useState<string | null>(
    null
  );

  React.useEffect(() => {
    if (!token) {
      setSubmissionError("Token de redefinição de senha não encontrado.");
    }
    if (!email) {
      setSubmissionError("Email não encontrado na URL.");
    }
  }, [token, email]);

  const onSubmit = async (data: ResetPasswordFormInputs) => {
    setSubmissionError(null);
    if (!token) {
      setSubmissionError("Token de redefinição de senha ausente.");
      return;
    }
    if (!email) {
      setSubmissionError("Email ausente na URL.");
      return;
    }

    try {
      await whenLoading(async () => {
        await resetPassword(token, email, data.newPassword);
        // Simulate API call success
        await new Promise((resolve) => setTimeout(resolve, 1000));
      });
      success("Sua senha foi redefinida com sucesso!");
      goToLogin();
    } catch (err) {
      if (err instanceof AxiosError) {
        if (isBadRequest(err)) {
          setSubmissionError(
            err.response?.data?.message || "Token inválido ou expirado."
          );
        } else {
          setSubmissionError("Ocorreu um erro. Por favor, tente novamente.");
        }
      } else {
        setSubmissionError("Ocorreu um erro inesperado.");
      }
      toastError("Erro ao redefinir senha.");
    }
  };

  return (
    <StarterContainer>
      <div className="space-y-6">
        <div className="space-y-2 text-center">
          <h1 className="text-3xl font-semibold">Redefinir Senha</h1>
          <p className="text-muted-foreground">
            Digite seu email e sua nova senha abaixo.
          </p>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <div className="px-3 py-2 border rounded-md bg-muted text-muted-foreground">
              {email || "Email não encontrado"}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nova Senha</Label>
            <Input
              id="newPassword"
              type="password"
              placeholder="********"
              {...register("newPassword")}
            />
            <FormError message={errors.newPassword?.message} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="********"
              {...register("confirmPassword")}
            />
            <FormError message={errors.confirmPassword?.message} />
          </div>
          {submissionError && <ErrorBox message={submissionError} />}
          <Button type="submit" className="w-full" isLoading={isLoading}>
            Redefinir Senha
          </Button>
        </form>
        <div className="text-center text-sm">
          Lembrou da sua senha?{" "}
          <Link to="/login" className="underline">
            Fazer login
          </Link>
        </div>
      </div>
    </StarterContainer>
  );
};
